import React, { useMemo, useState } from "react";
import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  useColorModeValue,
  Box,
  Flex,
  Button,
  IconButton,
} from "@chakra-ui/react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { EditIcon } from "@chakra-ui/icons";
import { FiFilter, FiTrash2 } from "react-icons/fi";
import FilterDrawer from "./FilterDrawer";
import { useDispatch } from 'react-redux';
import { useToastNotification } from 'utils/toast-utils';
import StatusUpdateModal from 'components/modals/StatusUpdateModal';
import { updateUserInvoiceStatus } from 'features/invoices/actions';

const InvoicesTable = ({ columnsData, tableData, activeTab, setActiveTab, onRowClick, onEditDraft, onFilterApply }) => {
  const columns = useMemo(() => columnsData, [columnsData]);
  const data = useMemo(() => tableData, [tableData]);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    Pending: false,
    Draft: false,
    'Under Review': false,
    Approved: false,
    Rejected: false,
    Funded: false,
    Incomplete: false,
    Deleted: false,
    Closed: false
  });
  const [appliedFilters, setAppliedFilters] = useState({
    Pending: false,
    Draft: false,
    'Under Review': false,
    Approved: false,
    Rejected: false,
    Funded: false,
    Incomplete: false,
    Deleted: false,
    Closed: false
  });
  const dispatch = useDispatch();
  const { showToast } = useToastNotification();
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(false);

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
  } = tableInstance;

  const getStatusColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    if (!normalizedStatus) return 'gray.500';
    
    switch (normalizedStatus) {
      case 'PENDING':
        return 'orange.600';
      case 'DRAFT':
        return 'gray.600';
      case 'UNDER REVIEW':
        return 'purple.600';
      case 'APPROVED':
        return 'green.500';
      case 'REJECTED':
        return 'red.500';
      case 'FUNDED':
        return 'teal.600';
      case 'INCOMPLETE':
        return 'yellow.500';
      case 'DELETED':
        return 'gray.500';
      case 'CLOSED':
        return 'blue.500';
      default:
        return 'gray.500';
    }
  };

  const getStatusBgColor = (status) => {
    const normalizedStatus = status?.toUpperCase();
    if (!normalizedStatus) return 'gray.100';
    
    switch (normalizedStatus) {
      case 'PENDING':
        return '#FFF3E0';  // Light orange/peach
      case 'DRAFT':
        return '#F5F5F5';  // Light gray
      case 'UNDER REVIEW':
        return '#F3E5FF';  // Light purple
      case 'APPROVED':
        return 'green.100';
      case 'REJECTED':
        return 'red.100';
      case 'FUNDED':
        return '#E0F2F1';  // Light teal
      case 'INCOMPLETE':
        return 'yellow.100';
      case 'DELETED':
        return 'gray.100';
      case 'CLOSED':
        return 'blue.100';
      default:
        return 'gray.100';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleApply = (selectedStatuses) => {
    const newAppliedFilters = { ...filters };
    setAppliedFilters(newAppliedFilters);
    onFilterApply(selectedStatuses);
    setIsFilterOpen(false);
  };

  const handleCancel = () => {
    setFilters({
      Pending: false,
      Draft: false,
      'Under Review': false,
      Approved: false,
      Rejected: false,
      Funded: false,
      Incomplete: false,
      Deleted: false,
      Closed: false
    });
    setAppliedFilters({
      Pending: false,
      Draft: false,
      'Under Review': false,
      Approved: false,
      Rejected: false,
      Funded: false,
      Incomplete: false,
      Deleted: false,
      Closed: false
    });
    onFilterApply([]);
    setIsFilterOpen(false);
  };

  const handleDeleteClick = (e, invoice) => {
    e.stopPropagation();
    setSelectedInvoice(invoice);
    setIsStatusModalOpen(true);
  };

  const handleStatusUpdate = async (status, reason = '') => {
    try {
      setLoading(true);
      await dispatch(updateUserInvoiceStatus({
        invoiceId: selectedInvoice._id,
        status: 'Deleted',
      })).unwrap();
      
      showToast('Success', 'Invoice deleted successfully', 'success');
      setIsStatusModalOpen(false);
    } catch (error) {
      showToast('Error', error.message || 'Failed to delete invoice', 'error');
    } finally {
      setLoading(false);
      setSelectedInvoice(null);
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pending';
      case 'DRAFT':
        return 'Draft';
      case 'UNDER REVIEW':
        return 'Under Review';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      case 'FUNDED':
        return 'Funded';
      case 'INCOMPLETE':
        return 'Incomplete';
      case 'DELETED':
        return 'Deleted';
      case 'CLOSED':
        return 'Closed';
      default:
        return status;
    }
  };

  const handleRemoveFilter = (status) => {
    const newFilters = { ...appliedFilters, [status]: false };
    setAppliedFilters(newFilters);
    setFilters(newFilters);
    const activeFilters = Object.entries(newFilters)
      .filter(([_, isActive]) => isActive)
      .map(([status]) => status);
    onFilterApply(activeFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(appliedFilters).filter(Boolean).length;
  };

  return (
    <Box>
      <Flex mb={4} position="sticky" top="0" bg="white" zIndex="2" align="center">
        {["All", "Draft"].map((tab) => (
          <Button
            key={tab}
            variant="ghost"
            bg={activeTab === tab ? "purple.50" : "transparent"}
            color={activeTab === tab ? "purple.500" : "gray.500"}
            onClick={() => setActiveTab(tab)}
            borderRadius="4px"
            mr={2}
            height="36px"
            fontSize="14px"
            fontWeight="500"
            _hover={{
              bg: activeTab === tab ? "purple.50" : "gray.50"
            }}
            _active={{
              bg: activeTab === tab ? "purple.50" : "gray.100"
            }}
          >
            {tab}
          </Button>
        ))}
        {activeTab !== "Draft" && (
          <Button
            leftIcon={<FiFilter />}
            variant={getActiveFilterCount() > 0 ? "solid" : "outline"}
            ml="auto"
            borderRadius="8px"
            fontWeight="500"
            size="md"
            px={4}
            py={2}
            color={getActiveFilterCount() > 0 ? "white" : "gray.600"}
            bg={getActiveFilterCount() > 0 ? "purple.500" : "white"}
            borderColor={getActiveFilterCount() > 0 ? "purple.500" : "gray.200"}
            boxShadow="sm"
            onClick={() => setIsFilterOpen(true)}
            _hover={{
              bg: getActiveFilterCount() > 0 ? "purple.600" : "gray.50",
              borderColor: getActiveFilterCount() > 0 ? "purple.600" : "gray.300"
            }}
            _active={{
              bg: getActiveFilterCount() > 0 ? "purple.700" : "gray.100"
            }}
            position="relative"
          >
            Filter By
            {getActiveFilterCount() > 0 && (
              <Flex
                position="absolute"
                top="-8px"
                right="-8px"
                bg="red.500"
                color="white"
                borderRadius="full"
                minW="20px"
                h="20px"
                align="center"
                justify="center"
                fontSize="xs"
                fontWeight="bold"
                boxShadow="sm"
              >
                {getActiveFilterCount()}
              </Flex>
            )}
          </Button>
        )}
      </Flex>
      
      {activeTab !== "Draft" && Object.entries(appliedFilters).some(([_, isActive]) => isActive) && (
        <Flex mb={4} gap={2} flexWrap="wrap">
          {Object.entries(appliedFilters).map(([status, isActive]) => {
            if (!isActive) return null;
            return (
              <Flex
                key={status}
                align="center"
                bg={getStatusBgColor(status)}
                color={getStatusColor(status)}
                px={3}
                py={1}
                borderRadius="full"
                fontSize="sm"
                fontWeight="500"
                boxShadow="sm"
                transition="all 0.2s"
                _hover={{
                  transform: "translateY(-1px)",
                  boxShadow: "md"
                }}
              >
                {getStatusDisplayName(status)}
                <Button
                  size="sm"
                  variant="ghost"
                  color={getStatusColor(status)}
                  ml={2}
                  p={0}
                  minW="auto"
                  h="auto"
                  onClick={() => handleRemoveFilter(status)}
                  _hover={{ 
                    bg: 'transparent', 
                    opacity: 0.8,
                    transform: "scale(1.1)"
                  }}
                  transition="all 0.2s"
                >
                  ×
                </Button>
              </Flex>
            );
          })}
        </Flex>
      )}

      <Box>
        <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
          <Thead position="sticky" top="0" bg="white">
            {headerGroups.map((headerGroup, index) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, index) => {
                  if (activeTab === "Draft" && column.Header === "Invoice Number") {
                    return null;
                  }
                  return (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      pe="10px"
                      key={index}
                      borderColor={borderColor}
                      whiteSpace="nowrap"
                    >
                      <Text
                        justifyContent="space-between"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        color="gray.600"
                      >
                        {column.render("Header")}
                      </Text>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              return (
                <Tr 
                  {...row.getRowProps()} 
                  key={index}
                  onClick={() => activeTab !== "Draft" && onRowClick(row.original)}
                  cursor={activeTab === "Draft" ? "default" : "pointer"}
                  _hover={{
                    bg: activeTab === "Draft" ? "transparent" : "gray.50"
                  }}
                >
                  {row.cells.map((cell, index) => {
                    if (activeTab === "Draft" && cell.column.Header === "Invoice Number") {
                      return null;
                    }
                    let data = cell.value;
                    
                    if (cell.column.Header === "Status") {
                      data = (
                        <Text
                          px={4}
                          py={1.5}
                          borderRadius="full"
                          bg={getStatusBgColor(cell.value)}
                          color={getStatusColor(cell.value)}
                          fontSize="sm"
                          fontWeight="500"
                          display="inline-block"
                          textTransform="capitalize"
                          whiteSpace="nowrap"
                        >
                          {cell.value?.split('_').map(word => word.toLowerCase()).join(' ')}
                        </Text>
                      );
                    } else if (cell.column.Header === "Actions") {
                      data = activeTab === "Draft" ? (
                        <Flex justify="center">
                          <Button
                            size="sm"
                            variant="ghost"
                            leftIcon={<EditIcon />}
                            color="purple.500"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditDraft(row.original);
                            }}
                            _hover={{
                              bg: "purple.50"
                            }}
                            aria-label="Edit Draft Invoice"
                          >
                            Edit Draft
                          </Button>
                        </Flex>
                      ) : activeTab === "All" ? (
                        <Flex justify="center">
                          {row.original.status === "Pending" ? (
                            <IconButton
                              icon={<FiTrash2 />}
                              variant="ghost"
                              size="sm"
                              colorScheme="red"
                              aria-label="Delete Invoice"
                              onClick={(e) => handleDeleteClick(e, row.original)}
                              _hover={{
                                bg: "red.50"
                              }}
                            />
                          ) : (
                            <IconButton
                              icon={<FiTrash2 />}
                              variant="ghost"
                              size="sm"
                              colorScheme="red"
                              aria-label="Delete Invoice"
                              isDisabled={true}
                              opacity={0.5}
                            />
                          )}
                        </Flex>
                      ) : null;
                    } else if (cell.column.Header === "System ID") {
                      data = (
                        <Text
                          color={activeTab === "Draft" ? "gray.500" : "purple.500"}
                          fontSize="sm"
                          fontWeight="500"
                          cursor={activeTab === "Draft" ? "default" : "pointer"}
                          _hover={{
                            textDecoration: activeTab === "Draft" ? "none" : "underline"
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeTab !== "Draft") {
                              onRowClick(row.original);
                            }
                          }}
                        >
                          {cell.value || "N/A"}
                        </Text>
                      );
                    } else if (cell.column.Header === "Expected Due Date") {
                      data = (
                        <Text color={textColor} fontSize="sm" fontWeight="500">
                          {cell.value ? formatDate(cell.value) : "N/A"}
                        </Text>
                      );
                    } else if (cell.column.Header === "Invoice Amount" || cell.column.Header === "Funded Amount" || cell.column.Header === "Collected Amount") {
                      data = (
                        <Text color={textColor} fontSize="sm" fontWeight="500">
                          {formatCurrency(cell.value)}
                        </Text>
                      );
                    }

                    return (
                      <Td
                        {...cell.getCellProps()}
                        key={index}
                        fontSize={{ sm: "14px" }}
                        minW={{ sm: "150px", md: "200px", lg: "auto" }}
                        borderColor={borderColor}
                        textAlign={cell.column.Header === "Actions" ? "center" : "left"}
                      >
                        {cell.column.Header === "Actions" ? data : data || "N/A"}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      <FilterDrawer
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        filters={filters}
        setFilters={setFilters}
        onApply={handleApply}
        onCancel={handleCancel}
      />
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false);
          setSelectedInvoice(null);
        }}
        onConfirm={handleStatusUpdate}
        status="Deleted"
        loading={loading}
        title="Delete Invoice"
        description="Are you sure you want to delete this invoice? This action cannot be undone."
        confirmButtonText="Delete"
        showReasonField={true}
        reasonLabel="Deletion Reason"
        reasonPlaceholder="Please provide a reason for deletion"
      />
    </Box>
  );
};

export default InvoicesTable;
